import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router';
import startCase from 'lodash/startCase';
import type { CountryCodesType } from '@axiom/const';
import {
  LanguageConst,
  CountryCodesConst,
  StateCodesConst,
  CandidatesConst,
} from '@axiom/const';
import {
  Button,
  Gutter,
  Grid,
  GridRow,
  GridColumn,
  IconButton,
  Layout,
  Tooltip,
  useApi,
  Form,
  Dropdown,
  CondensedHeader,
  CondensedMedium,
  CondensedLabel,
  ParagraphHeader,
  Suspense,
} from '@axiom/ui';
import type {
  Language,
  Candidate,
  CandidateRedux,
  Opportunity,
  Taxonomy,
  OpportunityCandidate,
} from '@axiom/validation';
import { CandidateYearsOfExperienceModel } from '@axiom/ui-models';

import { TagType } from '../../enums/generated/tag-type';
import { EnvUtil } from '../../utils/env-util';
import { CertificationsList } from '../CertificationsList/CertificationsList';
import { EducationList } from '../EducationList/EducationList';
import HorizontalRule from '../HorizontalRule/HorizontalRule';
import { TalentProfileEditForm } from '../TalentProfileEditForm/TalentProfileEditForm';
import sortLanguagesUtil from '../../utils/sort-languages-util';
import { TagRow, Tag } from '../../styled';
import { TalentAvailabilityNotes } from '../TalentAvailabilityNotes/TalentAvailabilityNotes';
import { AxiomForTalentUtil } from '../../utils/axiom-for-talent-util';
import { CandidateUtil } from '../../utils/candidate-util';
import { TalentOwnerUtil } from '../../utils/talent-owner-util';
import { WindowUtil } from '../../utils/window-util';
import { TalentInterviewAvailability } from '../TalentInterviewAvailability/TalentInterviewAvailability';
import { TalentOpportunityContainer } from '../TalentOpportunityContainer/TalentOpportunityContainer';
import { TalentOpportunityCardTypeNames } from '../TalentOpportunityCard/TalentOpportunityCard';
import { CandidateReputationSection } from '../CandidateReputationSection/CandidateReputationSection';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { TalentTagsApi } from '../../api/talent-tags-api';
import { TagApi } from '../../api/generated/tag-api';
import { UserApi } from '../../api/user-api';
import { CandidateTagsFormSchema } from '../../schema/candidate-tags-form-schema';
import { CandidatePerformanceSummary } from '../CandidatePerformanceSummary/CandidatePerformanceSummary';
import { useRefreshCandidate } from '../../hooks/useRefreshCandidate';
import { OpportunityTalentMatchSummary } from '../OpportunityTalentMatchSummary/OpportunityTalentMatchSummary';

import { TalentProfileDetailAvailability } from './TalentProfileDetailAvailability';
import { TalentProfileDetailSkills } from './TalentProfileDetailSkills';
import {
  DetailColumn,
  DetailContainer,
  DrawerContainer,
  FlagIconWrapper,
  SpanTalentFullName,
} from './TalentProfileDetailStyles';
import { TalentProfileDetailOppMatching } from './TalentProfileDetailOppMatching';
import { TalentProfileDetailPracticeArea } from './TalentProfileDetailPracticeArea';
import { TalentProfileDetailCompensation } from './TalentProfileDetailCompensation';
import { TalentProfileDetailTopFocusAreas } from './TalentProfileDetailTopFocusAreas';

const { AbbreviatedCountryCode } = CountryCodesConst;
const { AbbreviatedStateCode } = StateCodesConst;

export interface TalentProfileDetailProps {
  name?: string;
  isDetail: boolean;
  candidate: CandidateRedux | Candidate;
  isEnvoySalesUser: boolean;
  taxonomy: Taxonomy;
  opportunityCandidatesByTalent: OpportunityCandidate[];
  opportunityId?: Opportunity['id'];
  isBenchCandidate?: boolean;
}

interface TagFieldType {
  label: string;
  value: string;
}

export const TalentProfileDetail = ({
  name = 'PROFILE_CONTAINER',
  isDetail = false,
  candidate,
  isEnvoySalesUser = false,
  taxonomy,
  opportunityCandidatesByTalent,
  opportunityId,
  isBenchCandidate = false,
}: TalentProfileDetailProps) => {
  const { id, view } = useParams();
  const [{ data: user }] = useApi(UserApi.readSessionUser());
  const [{ data: rawTags }] = useApi(TagApi.readTags());
  const { barredLocations, ownerUser, addressCountry, addressState } =
    candidate;

  const hideTalentOwner = TalentOwnerUtil.isHiddenFromSales({
    isEnvoySalesUser,
    profileStatus: candidate.profileStatus,
  });
  const talentOwnerDisplay = ownerUser ? ownerUser.fullName : '--';

  const [{ data: opportunityCandidates } = { data: null }] = useApi(
    id && view === 'talent'
      ? OpportunityLegacyApi.readOpportunityCandidates(id)
      : undefined
  );

  const [{ data: candidateTags }] = useApi(
    TalentTagsApi.readCandidateTags(candidate.id)
  );
  const { refreshCandidate } = useRefreshCandidate(candidate.id);
  const [showTalentProfileEditForm, setShowTalentProfileEditForm] = useState<
    CandidateRedux | Candidate | null
  >(null);
  const tags = useMemo(() => {
    return rawTags
      .filter(d => d.type === TagType.Tag)
      .map(d => ({
        value: d.id,
        label: d.name ?? '',
        type: d.type,
      }));
  }, [rawTags]);
  const Container = isDetail ? DetailContainer : DrawerContainer;
  const Column = isDetail ? DetailColumn : 'div';
  const canEditProfile = !isEnvoySalesUser;
  const homeLocation = (() => {
    const country = addressCountry
      ? AbbreviatedCountryCode(addressCountry as CountryCodesType) ||
        addressCountry
      : '';
    const state = addressState
      ? AbbreviatedStateCode(addressState) || addressState
      : '';

    if (!state && addressCountry) {
      return addressCountry;
    } else if (!country && addressState) {
      return addressState;
    }

    return [country, state].filter(loc => !!loc).join(' - ');
  })();

  const talentFullNameWithFlag = (
    <CondensedMedium>
      {candidate.isFlagged ? <FlagIconWrapper /> : null}
      <SpanTalentFullName>{candidate.calculatedDisplayName}</SpanTalentFullName>
    </CondensedMedium>
  );

  const emailsUrl = (() => {
    const addresses = [candidate.personalEmail, candidate.activeWorkEmail]
      .filter(Boolean)
      .join('; ');
    return addresses.length > 0 ? `mailto:${addresses}` : null;
  })();
  const fieldTags = useMemo(() => {
    const rawCandidateTags = candidate.tags;

    if (!rawCandidateTags || rawCandidateTags.length === 0) return [];

    const tagObjs = rawCandidateTags.reduce<Record<string, TagFieldType>>(
      (tObj, candidateTag) => {
        const foundTag = tags.find(fTag => fTag.value === candidateTag.id);

        if (foundTag) {
          tObj[foundTag.value] = foundTag;
        }

        return tObj;
      },
      {}
    );

    return Object.values(tagObjs);
  }, [candidate.tags, tags]);
  const formattedAddress = (() => {
    const state = candidate.addressState || '';
    const zip = candidate.addressZip || '';

    const parts = [
      candidate.address1,
      candidate.address2,
      candidate.addressCity,
      isEnvoySalesUser ? state : `${state} ${zip}`.trim(),
      candidate.addressCountry,
    ];

    return parts.filter(part => !!part).join(', ');
  })();
  const barredLocationNames = barredLocations?.map(bl => bl.name) ?? [];
  const candidateLanguages = candidate.languages
    ?.sort(sortLanguagesUtil)
    .map(lang => {
      const parts = [
        lang.name,
        startCase(
          LanguageConst.LANGUAGE_PROFICIENCIES[
            lang.languageProficiency as keyof Language['languageProficiency']
          ]
        ),
        startCase(lang.languageSkill ?? ''),
        lang.languageCEFR,
      ];
      return parts.filter(Boolean).join('; ');
    });

  const closeTalentProfileEditModal = () => {
    setShowTalentProfileEditForm(null);
  };

  useEffect(() => {
    const query = WindowUtil.getUrlQuery();
    if (query.editProfile) {
      setShowTalentProfileEditForm(candidate);
      WindowUtil.setUrlQuery(
        {
          ...query,
          editProfile: undefined,
        },
        true
      );
    }
  }, []);

  // Skills, tags, education, and certifications. When viewing talent in an
  // opportunity context these move to the bottom of the left column so the
  // match summary can occupy the right side; otherwise they stay on the right.
  const profileExtras = (
    <>
      <TalentProfileDetailSkills
        name="TALENT_PROFILE_DETAIL_SKILLS"
        candidate={candidate}
      />
      <CondensedHeader>Tags</CondensedHeader>
      <Gutter bottom="16px" />
      {canEditProfile ? (
        // Key the form by candidate.id so it remounts whenever the side
        // drawer switches to a different candidate. Without this, two
        // candidates that both have no tags share `initialValues` (both
        // `[]`) and Formik's enableReinitialize has no change to detect,
        // leaving the prior candidate's selection visible on the next
        // candidate's profile (BACK-7801).
        <div data-candidate-id={candidate.id}>
          <Form
            key={candidate.id}
            name="CANDIDATE_TAGS_FORM"
            schema={CandidateTagsFormSchema}
            submitOnChange
            onSubmit={async changedData => {
              await TalentTagsApi.updateCandidateTags({
                candidateId: candidate.id,
                body: changedData.candidateTags ?? [],
              });
              await refreshCandidate(candidate.id);
            }}
            initialValues={{
              candidateTags: candidateTags.map(item => item.id),
            }}
          >
            {() => {
              return (
                <Dropdown
                  displayKey="label"
                  name="candidateTags"
                  options={tags}
                  valueKey="value"
                />
              );
            }}
          </Form>
        </div>
      ) : (
        fieldTags.length > 0 && (
          <TagRow marginBottom={12}>
            {fieldTags.map(tag => (
              <Tag key={tag.value}>{tag.label}</Tag>
            ))}
          </TagRow>
        )
      )}
      <EducationList name="EDUCATION_LIST_CONTAINER" candidate={candidate} />
      <CertificationsList
        name="CERTIFICATIONS_LIST_CONTAINER"
        candidate={candidate}
      />
    </>
  );

  return (
    <Container data-test={name}>
      <Column data-test="DETAILS">
        <Layout position="right" horizontalGutter="8px">
          {canEditProfile ? (
            <Button
              variation="outline"
              onClick={() => {
                setShowTalentProfileEditForm(candidate);
              }}
              name="TALENTPROFILEEDITPEN"
            >
              Edit Talent Info
            </Button>
          ) : null}
          {CandidateUtil.getBioDisposition(candidate) !== 'skip' && (
            <Button
              name="OPEN_BIO_BUTTON"
              variation="outline"
              onClick={() => {
                if (CandidateUtil.getBioDisposition(candidate) === 'named') {
                  AxiomForTalentUtil.openBio(candidate.id);
                } else {
                  AxiomForTalentUtil.openAnonymousBio(candidate.id);
                }
              }}
            >
              Open Bio
            </Button>
          )}
        </Layout>
        <Gutter bottom="16px" />
        <TalentAvailabilityNotes
          name="TALENT_AVAILABILITY_NOTES"
          candidate={candidate}
        />
        <Gutter bottom="8px" />
        <TalentInterviewAvailability
          name="TALENT_INTERVIEW_AVAILABILITY"
          candidate={candidate}
          user={user}
          opportunityCandidate={opportunityCandidates?.find(
            co => co.candidateId === candidate.id
          )}
        />
        <Gutter bottom="8px" />
        <TalentOpportunityContainer
          name="TALENT_OPPORTUNITY_CONTAINER"
          type={TalentOpportunityCardTypeNames.currentEngagements}
          candidateOpportunities={opportunityCandidatesByTalent.filter(
            cOpp => cOpp.candidateStatus === 'Engaged'
          )}
          candidate={candidate}
        />
        {opportunityId ? null : (
          <Gutter top="16px" bottom="8px">
            <TalentProfileDetailOppMatching
              name="OPP_MATCHING_SECTION"
              candidateId={candidate.id}
              candidateName={candidate.calculatedDisplayName}
            />
          </Gutter>
        )}
        <HorizontalRule />
        {CandidatesConst.ValidFulfillmentCompletionStatuses.includes(
          candidate.profileStatus
        ) && (
          <>
            <Gutter bottom="24px">
              <ParagraphHeader>Axiom Performance</ParagraphHeader>
            </Gutter>
            <CandidateReputationSection
              name="CANDIDATE_REPUTATION_SECTION"
              candidate={candidate}
            />
            <Gutter bottom="16px" />
            <CandidatePerformanceSummary
              name="CANDIDATE_PERFORMANCE_SUMMARY"
              candidateId={candidate.id}
            />
            <HorizontalRule />
          </>
        )}
        <Button
          name="GO_TO_FEEDBACK"
          toTab={`/talent-detail/${candidate.id}/feedback`}
          icon="open"
        >
          View All Feedback
        </Button>
        <HorizontalRule />
        {showTalentProfileEditForm ? (
          <Suspense>
            <TalentProfileEditForm
              name="TALENT_PROFILE_EDIT_FORM"
              candidate={candidate}
              onClose={closeTalentProfileEditModal}
            />
          </Suspense>
        ) : null}
        <Gutter bottom="16px">
          <CondensedHeader name="professional-details-header">
            Professional Details
          </CondensedHeader>
        </Gutter>
        <Grid>
          <GridRow gutterBottom="16px">
            <GridColumn width={isDetail ? 6 : 4}>
              <Gutter bottom="16px">
                <CondensedLabel>Name</CondensedLabel>
                {talentFullNameWithFlag}
              </Gutter>
            </GridColumn>
            <GridColumn width={isDetail ? 6 : 4}>
              <Gutter bottom="16px">
                <CondensedLabel>Integration Id</CondensedLabel>
                <CondensedMedium>{candidate.integrationId}</CondensedMedium>
              </Gutter>
            </GridColumn>
            <GridColumn width={isDetail ? 6 : 4}>
              <Gutter bottom="16px">
                <Layout inline position="middle">
                  <CondensedLabel>Employment Status</CondensedLabel>
                  <Tooltip>
                    This is the candidate's status in our HR systems. If you
                    feel this status is incorrect, please{' '}
                    <a
                      href={EnvUtil.reportAnIssueUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      submit a ticket for HRIS
                    </a>
                  </Tooltip>
                </Layout>
                <CondensedMedium>{candidate.employmentStatus}</CondensedMedium>
              </Gutter>
            </GridColumn>
            <GridColumn width={isDetail ? 6 : 4}>
              <Gutter bottom="16px">
                <CondensedLabel>Talent Owner</CondensedLabel>
                <CondensedMedium>
                  {hideTalentOwner
                    ? "Talent Owner hidden due to talent's profile status."
                    : talentOwnerDisplay}
                </CondensedMedium>
              </Gutter>
            </GridColumn>
            <GridColumn width={isDetail ? 6 : 4}>
              <Gutter bottom="16px">
                <CondensedLabel>Bar Admissions</CondensedLabel>
                {barredLocationNames.length > 0 ? (
                  barredLocationNames.map(locationName => (
                    <CondensedMedium
                      key={locationName}
                      name="BarredAdmissionsDisplay"
                    >
                      {locationName}
                    </CondensedMedium>
                  ))
                ) : (
                  <CondensedMedium name="BarredAdmissionsDisplay">
                    --
                  </CondensedMedium>
                )}
              </Gutter>
            </GridColumn>
            <GridColumn width={isDetail ? 6 : 4}>
              <Gutter bottom="16px">
                <CondensedLabel>Home Location</CondensedLabel>
                <CondensedMedium>{homeLocation}</CondensedMedium>
              </Gutter>
            </GridColumn>
            <GridColumn width={12}>
              <Gutter bottom="16px">
                <CondensedLabel>Practice Area & Focus</CondensedLabel>
                <TalentProfileDetailPracticeArea
                  name="TALENT_PROFILE_DETAIL_PRACTICE_AREA"
                  candidate={candidate}
                />
              </Gutter>
            </GridColumn>
            <GridColumn width={12}>
              <Gutter bottom="16px">
                <CondensedLabel>Years of Experience</CondensedLabel>
                <CondensedMedium>
                  {new CandidateYearsOfExperienceModel(
                    candidate.yearsOfExperience
                  ).formatYearsOfExperience() || '--'}
                </CondensedMedium>
              </Gutter>
            </GridColumn>
            <GridColumn width={12}>
              <Gutter bottom="16px">
                <CondensedLabel>Employee Type</CondensedLabel>
                <CondensedMedium>{candidate.employeeType}</CondensedMedium>
              </Gutter>
            </GridColumn>
            <GridColumn width={12}>
              <Gutter bottom="16px">
                <CondensedLabel>Languages</CondensedLabel>
                {candidateLanguages && candidateLanguages.length > 0 ? (
                  candidateLanguages.map(lang => (
                    <CondensedMedium key={lang}>{lang}</CondensedMedium>
                  ))
                ) : (
                  <CondensedMedium>--</CondensedMedium>
                )}
              </Gutter>
            </GridColumn>
          </GridRow>
        </Grid>
        <HorizontalRule />
        <Gutter bottom="16px">
          <CondensedHeader>Contact Information</CondensedHeader>
        </Gutter>
        <Grid>
          <GridRow gutterBottom="20px">
            <GridColumn width={isDetail ? 6 : 4}>
              <CondensedLabel>Phone</CondensedLabel>
              <Gutter bottom="16px" />
              <CondensedMedium>
                Cell {candidate.mobilePhone ?? ' -- '}
              </CondensedMedium>
              <CondensedMedium>
                Home {candidate.homePhone ?? ' -- '}
              </CondensedMedium>
            </GridColumn>
            <GridColumn width={isDetail ? 6 : 8}>
              <Layout position="space-between top">
                <CondensedLabel>Email</CondensedLabel>
                <IconButton
                  name="EMAIL_ICON_BUTTON"
                  icon="envelope"
                  to={emailsUrl ?? '#'}
                  pattern="secondary"
                  variation="minimal"
                />
              </Layout>
              <CondensedMedium>
                Personal{' '}
                {candidate.personalEmail ? (
                  <a
                    data-test="PERSONALEMAIL"
                    href={`mailto:${candidate.personalEmail}`}
                  >
                    {candidate.personalEmail}
                  </a>
                ) : (
                  <span data-test="PERSONALEMAIL">--</span>
                )}
              </CondensedMedium>
              <CondensedMedium>
                Axiom{' '}
                {candidate.activeWorkEmail ? (
                  <a
                    data-test="WORKEMAIL"
                    href={`mailto:${candidate.activeWorkEmail}`}
                  >
                    {candidate.activeWorkEmail}
                  </a>
                ) : (
                  <span data-test="WORKEMAIL">--</span>
                )}
              </CondensedMedium>
            </GridColumn>
          </GridRow>
          <GridRow columns={1}>
            <GridColumn>
              <CondensedLabel>Address</CondensedLabel>
              <CondensedMedium>{formattedAddress}</CondensedMedium>
            </GridColumn>
          </GridRow>
        </Grid>

        <HorizontalRule />
        <TalentProfileDetailCompensation
          name="TALENT_PROFILE_DETAIL_COMPENSATION"
          talent={candidate}
          taxonomy={taxonomy}
        />
        <HorizontalRule />
        <TalentProfileDetailAvailability
          name="TALENT_PROFILE_DETAIL_AVAILABILITY"
          candidate={candidate}
        />

        <TalentProfileDetailTopFocusAreas
          name="TALENT_PROFILE_DETAIL_TOP_FOCUS_AREAS"
          candidateId={candidate.id}
        />

        {opportunityId ? (
          <>
            <HorizontalRule />
            {profileExtras}
          </>
        ) : null}

        {!isDetail && <HorizontalRule data-test="BOTTOM_DETAIL_DIVIDER" />}
      </Column>
      <Column data-test="PROFILE_SUMMARY_COLUMN">
        {opportunityId ? (
          <Suspense>
            <OpportunityTalentMatchSummary
              name="OPPORTUNITY_TALENT_MATCH_SUMMARY"
              opportunityId={opportunityId}
              candidateId={candidate.id}
              isBenchCandidate={isBenchCandidate}
            />
          </Suspense>
        ) : (
          profileExtras
        )}
      </Column>
    </Container>
  );
};
